/**
 * Programming-topic detection and Pass 2 suggested-artifact diagnostics (mirrors backend).
 */

const PROGRAMMING_TOPIC_RE = new RegExp(
  '\\b('
  + 'binary\\s*search|bubble\\s*sort|quick\\s*sort|merge\\s*sort|'
  + 'recursion|recursive|linked\\s*list|stack|queue|heap|tree|graph|'
  + 'dfs|bfs|breadth[- ]first|depth[- ]first|dynamic\\s*programming|'
  + 'algorithm|sorting|searching|data\\s*structure|'
  + 'programming|program|code|function|loop|array|pointer|'
  + 'palindrome|traversal|complexity|big\\s*o'
  + ')\\b',
  'i',
);

const CODE_ARTIFACT_TYPES = new Set([
  'code example',
  'code walkthrough',
  'algorithm walkthrough',
]);

export function isProgrammingTopic(...texts) {
  const blob = texts.filter(Boolean).map(String).join(' ');
  return Boolean(blob) && PROGRAMMING_TOPIC_RE.test(blob);
}

export function programmingContextFromIntake(intake = {}, conceptTitle = '') {
  const topic = String(intake.topic || '').trim();
  const objective = String(intake.learning_objective || '').trim();
  const keyPoints = (intake.key_points || []).join(' ');
  const includeCode = intake.include_code === true;
  const isProg = isProgrammingTopic(topic, objective, keyPoints, conceptTitle);

  return {
    is_programming_topic: isProg,
    suggest_code_artifacts: isProg && includeCode,
    suggest_dry_run: isProg,
    include_code: includeCode,
  };
}

export function missingSuggestedArtifacts(artifacts = [], ctx = {}) {
  if (!ctx.is_programming_topic) return [];

  const present = new Set(
    (artifacts || [])
      .map((a) => String(a?.artifact_type || '').trim().toLowerCase())
      .filter(Boolean),
  );
  const missing = [];

  if (ctx.suggest_code_artifacts) {
    const hasCode = [...present].some((t) => CODE_ARTIFACT_TYPES.has(t));
    if (!hasCode) missing.push('Code Walkthrough or Algorithm Walkthrough');
  }
  if (ctx.suggest_dry_run && !present.has('dry run')) {
    missing.push('Dry Run');
  }
  return missing;
}

export function suggestedArtifactGaps(conceptTree = [], intake = {}) {
  const base = programmingContextFromIntake(intake);
  if (!base.is_programming_topic) return [];

  const rows = [];

  function walk(nodes, prefix = '') {
    for (const node of nodes || []) {
      const title = String(node?.title || '').trim();
      if (!title) continue;
      const path = prefix ? `${prefix}/${title}` : title;
      const ctx = programmingContextFromIntake(intake, title);
      const gaps = missingSuggestedArtifacts(node.instructional_artifacts, ctx);
      if (gaps.length) {
        rows.push({ concept_path: path, concept_title: title, suggested_missing: gaps });
      }
      walk(node.children, path);
    }
  }

  walk(conceptTree);
  return rows;
}

export function hasSuggestedArtifactGaps(project) {
  const orch = project?.orchestration;
  if (orch?.suggested_artifact_gaps?.length) return true;
  const intake = project?.intake || {};
  const tree = orch?.concept_tree || [];
  return suggestedArtifactGaps(tree, intake).length > 0;
}

export function suggestedArtifactGapsForProject(project) {
  const orch = project?.orchestration;
  if (orch?.suggested_artifact_gaps?.length) {
    return orch.suggested_artifact_gaps;
  }
  return suggestedArtifactGaps(orch?.concept_tree || [], project?.intake || {});
}
